import React, { useEffect, useMemo, useState } from 'react';
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaPlus,
  FaStore,
  FaUserTie,
  FaPauseCircle,
  FaTruck,
  FaSearch,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import api from '../lib/api';
import './CSS/AdminPremium.css';

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === 'success'
      ? FaCheckCircle
      : notice.type === 'warning' || notice.type === 'error'
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`admin-toast ${notice.type}`}
      role={notice.type === 'warning' || notice.type === 'error' ? 'alert' : 'status'}
      aria-live={notice.type === 'warning' || notice.type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="admin-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="admin-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="admin-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm, saving }) {
  if (!open) return null;

  return (
    <div className="premium-modal-overlay" role="presentation">
      <div className="premium-modal" role="dialog" aria-modal="true" aria-labelledby="vendor-modal-title">
        <div className="premium-modal-header">
          <div>
            <h5 id="vendor-modal-title">{title}</h5>
            <p>{message}</p>
          </div>
          <button type="button" className="btn-close" onClick={onCancel} aria-label="Close" />
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={saving}>
            <FaTrashAlt aria-hidden="true" className="me-2" />
            {saving ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`premium-summary-card ${tone}`}>
      <div className="premium-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="premium-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

export default function VendorMaster() {
  const token = localStorage.getItem('token');

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    id: null,
    name: '',
    contact_person: '',
    phone: '',
    gst: '',
    address: '',
    is_active: 1,
  });

  const rowsPerPage = 5;

  const showNotice = (type, title, message) => {
    setNotice({ id: Date.now(), type, title, message });
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === 'warning' || notice.type === 'error' ? 5000 : 3200
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/vendors', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(res.data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      showNotice('error', 'Load failed', 'Failed to load vendors.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      id: null,
      name: '',
      contact_person: '',
      phone: '',
      gst: '',
      address: '',
      is_active: 1,
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showNotice('warning', 'Vendor name required', 'Please enter a vendor name.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        gst: form.gst.trim() || null,
        address: form.address.trim() || null,
        is_active: form.is_active ? 1 : 0,
      };

      if (form.id) {
        await api.put(`/api/vendors/${form.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice('success', 'Updated', 'Vendor updated successfully.');
      } else {
        await api.post('/api/vendors', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice('success', 'Saved', 'Vendor added successfully.');
      }

      resetForm();
      await fetchVendors();
    } catch (err) {
      console.error('Error saving vendor:', err);
      showNotice('error', 'Save failed', err?.response?.data?.error || 'Failed to save vendor.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (v) => {
    setForm({
      id: v.id,
      name: v.name || '',
      contact_person: v.contact_person || '',
      phone: v.phone || '',
      gst: v.gst || '',
      address: v.address || '',
      is_active: v.is_active === 1 || v.is_active === true,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const requestDelete = (vendor) => {
    setDeleteTarget(vendor);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setSaving(true);
      await api.delete(`/api/vendors/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteTarget(null);
      showNotice('success', 'Deleted', 'Vendor removed successfully.');
      await fetchVendors();
    } catch (err) {
      console.error('Error deleting vendor:', err);
      showNotice('error', 'Delete failed', err?.response?.data?.error || 'Failed to delete vendor.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (v) => {
    try {
      setSaving(true);
      const payload = {
        name: v.name,
        contact_person: v.contact_person,
        phone: v.phone,
        gst: v.gst,
        address: v.address,
        is_active: v.is_active ? 0 : 1,
      };

      await api.put(`/api/vendors/${v.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showNotice('success', 'Updated', v.is_active ? 'Vendor marked inactive.' : 'Vendor marked active.');
      fetchVendors();
    } catch (err) {
      console.error('Error toggling vendor:', err);
      showNotice('error', 'Update failed', err?.response?.data?.error || 'Failed to update vendor status.');
    } finally {
      setSaving(false);
    }
  };

  const filteredVendors = useMemo(() => {
    if (!search.trim()) return vendors;

    const s = search.toLowerCase();
    return vendors.filter((v) => {
      return (
        (v.name || '').toLowerCase().includes(s) ||
        (v.contact_person || '').toLowerCase().includes(s) ||
        (v.phone || '').toLowerCase().includes(s) ||
        (v.gst || '').toLowerCase().includes(s) ||
        (v.address || '').toLowerCase().includes(s)
      );
    });
  }, [vendors, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / rowsPerPage));
  const visibleVendors = filteredVendors.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const stats = {
    total: vendors.length,
    active: vendors.filter((v) => v.is_active).length,
    inactive: vendors.filter((v) => !v.is_active).length,
  };

  return (
    <div className="admin-premium-page vendor-page">
      <section className="premium-hero">
        <div>
          <span className="premium-eyebrow">Master data</span>
          <h1>Vendor Master</h1>
          <p>Keep vendors, contacts, and business details in a polished master list that feels like the rest of the app.</p>
        </div>
        <div className="premium-tab-strip">
          <button className="btn btn-primary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <FaPlus aria-hidden="true" className="me-2" />
            Add Vendor
          </button>
        </div>
      </section>

      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="premium-summary-grid">
        <StatCard tone="blue" icon={FaStore} label="Total Vendors" value={stats.total} caption="Registered vendor partners" />
        <StatCard tone="green" icon={FaTruck} label="Active" value={stats.active} caption="Ready for transactions" />
        <StatCard tone="amber" icon={FaPauseCircle} label="Inactive" value={stats.inactive} caption="Temporarily paused vendors" />
        <StatCard tone="red" icon={FaUserTie} label="Current Form" value={form.id ? 'Editing' : 'Adding'} caption="Vendor master workflow" />
      </section>

      <div className="premium-context-strip">
        <div className="flex-grow-1">
          <label className="form-label">Search</label>
          <div className="input-group">
            <span className="input-group-text">
              <FaSearch aria-hidden="true" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, phone, GST..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="premium-context-chip">
          <span>Status</span>
          <strong>Ready for quick edits</strong>
        </div>
      </div>

      <div className="premium-card mb-3">
        <h5 className="mb-3">{form.id ? 'Edit Vendor' : 'Add Vendor'}</h5>

        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">Vendor Name *</label>
            <input
              type="text"
              className="form-control"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Eg: BESCOM, Rent Owner, Maintenance"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Contact Person</label>
            <input
              type="text"
              className="form-control"
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Contact Number</label>
            <input
              type="text"
              className="form-control"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">GST Number</label>
            <input
              type="text"
              className="form-control"
              value={form.gst}
              onChange={(e) => setForm({ ...form, gst: e.target.value })}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Address</label>
            <textarea
              rows="2"
              className="form-control"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div className="col-md-2 d-flex align-items-center">
            <div className="form-check mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="vendorActiveChk"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="vendorActiveChk">
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex gap-2 mt-3">
          <button className="btn btn-success" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : form.id ? 'Update Vendor' : 'Save Vendor'}
          </button>
          {form.id && (
            <button className="btn btn-secondary" onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      <div className="table-responsive premium-table-shell">
        {loading ? (
          <div className="text-center py-3 text-muted">Loading...</div>
        ) : (
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Vendor Name</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>GST</th>
                <th>Address</th>
                <th>Status</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleVendors.map((v) => (
                <tr key={v.id}>
                  <td>{v.name}</td>
                  <td>{v.contact_person || '-'}</td>
                  <td>{v.phone || '-'}</td>
                  <td>{v.gst || '-'}</td>
                  <td style={{ maxWidth: 250, whiteSpace: 'pre-wrap' }}>{v.address || '-'}</td>
                  <td>
                    {v.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-danger">Inactive</span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <button className="btn btn-sm btn-warning" onClick={() => handleEdit(v)}>
                        Edit
                      </button>
                      <button
                        className={`btn btn-sm ${v.is_active ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        onClick={() => handleToggleActive(v)}
                      >
                        {v.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => requestDelete(v)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!visibleVendors.length && (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No vendors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        <div className="premium-pagination">
          <div className="page-nav">
            <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Prev
            </button>
          </div>
          <div className="page-chips">
            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
              <button
                key={page}
                type="button"
                className={page === currentPage ? 'active' : ''}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
          <div className="page-nav">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete vendor?"
        message={`Remove ${deleteTarget?.name || 'this vendor'} from the master list.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        saving={saving}
      />
    </div>
  );
}
